import { toLower } from 'lodash'
import { toast } from 'react-toastify'

import { SMALL_CASE_LETTERS_NUMBERS_REGEX, URL_REGEX } from '~/constants'
import { isChatbotRoutePath } from '~/features/chatbot/utils'
import { EAuthErrorName, ECognitoGroups } from '~/models/auth'

type TParams = {
  fn: () => Promise<void>
  finallyFn?: () => void
  errorFn?: (error: string, name?: EAuthErrorName) => void
}

/** Save a Blob as a downloaded file (same pattern as contacts / conversation downloads). */
export function triggerBlobFileDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export function triggerMultipleCsvDownloads(
  files: Array<{ filename: string; csv: string }>,
) {
  files.forEach((file, index) => {
    window.setTimeout(() => {
      const blob = new Blob([`\uFEFF${file.csv}`], {
        type: 'text/csv;charset=utf-8',
      })
      triggerBlobFileDownload(blob, file.filename)
    }, index * 120)
  })
}

const asyncTryCatch = async ({ fn, finallyFn, errorFn }: TParams) => {
  try {
    await fn()
  } catch (error) {
    if (errorFn) {
      const err = error as { name?: string; message?: string }
      let errorMessage = 'Something went wrong!... Please try again later.'
      if (err.name === EAuthErrorName.CodeMismatchException) {
        errorMessage = 'Code is invalid.'
      } else if (err.name === EAuthErrorName.ExpiredCodeException) {
        errorMessage = 'The code has expired. Please request a new one.'
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      errorFn(errorMessage, err.name as EAuthErrorName)
    } else {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Something went wrong!... Please try again later.',
      )
    }
  } finally {
    if (finallyFn) {
      finallyFn()
    }
  }
}

export const propertyOf = <T>(name: keyof T) => name

export default asyncTryCatch

export const formatWebsiteLink = (inputLink?: string): string => {
  if (!inputLink) return ''

  return inputLink.trim().replace(URL_REGEX, 'https://')
}

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

export const sanitizedBusinessName = (link: string) => {
  return toLower(link.replace(SMALL_CASE_LETTERS_NUMBERS_REGEX, ''))
}

export const getTierFriendlyName = (tier?: ECognitoGroups | string) => {
  switch (tier) {
    case ECognitoGroups.customerBasicPlan:
      return 'Basic'
    case ECognitoGroups.customerPlusPlan:
      return 'Plus'
    case ECognitoGroups.customerFreePlan:
      return 'Free'
    case ECognitoGroups.customerBasicFreeTrialPlan:
      return 'Basic Free Trial'
    default:
      return 'N/A'
  }
}

export const addCookieTrackerScriptIfNeeded = () => {
  // InMobi Choice. Consent Manager Tag v3.0 (for TCF 2.2)
  const isScriptAdded = document.querySelector(
    'script[src="/js/cookieTracker.js"]',
  )

  const isChatPage = isChatbotRoutePath(window.location.pathname)

  if (isScriptAdded || isChatPage) {
    return
  }

  const inmobiConsentManagerTag = document.createElement('script')
  inmobiConsentManagerTag.type = 'text/javascript'
  inmobiConsentManagerTag.async = true
  inmobiConsentManagerTag.src = '/js/cookieTracker.js'
  document.body.appendChild(inmobiConsentManagerTag)
}
